// Import React helpers
import { useEffect, useState } from "react";

// Import application layout configuration
import { AppLayoutConfig } from "global/config/AppLayoutConfig";

// Import the application configuration getter
import { GetAppConfig } from "global/config/AppConfigStore";



// Export the reusable desktop detection hook
export const useIsDesktop = () => {

    // Store whether unsupported screen sizes are allowed
    const [allowUnsupportedScreenSizes, setAllowUnsupportedScreenSizes] = useState(false);

    // Store whether the environment configuration has finished loading
    const [hasLoadedAppConfig, setHasLoadedAppConfig] = useState(false);

    // Store whether the current viewport is desktop sized
    const [isDesktop, setIsDesktop] = useState(
        window.innerWidth >= AppLayoutConfig.minimumDesktopWidth
    );



    // Load the application environment configuration
    useEffect(() => {

        // Track whether the component is still mounted
        let isMounted = true;



        // Create the configuration loading function
        const loadAppConfig = async () => {

            try {

                // Get the current application configuration
                const appConfig = await GetAppConfig();

                // Stop if the component has unmounted
                if (!isMounted) {

                    return;

                }

                // Check whether the application is running in development
                const isDevelopment =
                    appConfig.environment === "development";

                // Store whether unsupported screen sizes are allowed
                setAllowUnsupportedScreenSizes(isDevelopment);

            } catch (error) {

                // Log the configuration loading error
                console.error("Unable to load the application configuration", error);

                // Keep the mobile override disabled when configuration loading fails
                setAllowUnsupportedScreenSizes(false);

            } finally {

                // Stop if the component has unmounted
                if (!isMounted) {

                    return;

                }

                // Mark the application configuration as loaded
                setHasLoadedAppConfig(true);

            }

        };



        // Load the application configuration
        loadAppConfig();



        // Clean up when the component unmounts
        return () => {

            // Mark the component as unmounted
            isMounted = false;

        };

    }, []);



    // Watch for viewport size changes
    useEffect(() => {

        // Update the current desktop state
        const handleResize = () => {

            // Check whether the viewport meets the minimum desktop width
            const viewportIsDesktop =
                window.innerWidth >= AppLayoutConfig.minimumDesktopWidth;

            // Store the latest desktop state
            setIsDesktop(viewportIsDesktop);

        };



        // Run the check immediately
        handleResize();

        // Listen for browser resize events
        window.addEventListener("resize", handleResize);



        // Remove the resize listener when the component unmounts
        return () => {

            // Remove the browser resize listener
            window.removeEventListener("resize", handleResize);

        };

    }, []);



    // Allow any screen width while running in development
    const canViewApplication =
        allowUnsupportedScreenSizes || isDesktop;

    // Return the viewport support state and loading state
    return {

        // Return whether the application can be viewed
        canViewApplication,

        // Return whether the environment configuration has loaded
        hasLoadedAppConfig,

    };

};